import React, { useMemo, useEffect, useState, createRef, useContext } from 'react';
import { FullSizeSkeleton } from "@hm-group/fabric-components/components/Skeleton/FullSizeSkeleton";
import { default as breakpoints } from "@hm-group/fabric-tokens/breakpoints";
import getImageUrlBasedOnContainerWidth from '@hm-group/frontend-utils/image/getImageUrlBasedOnContainerWidth';
import imageIdToAkamaiSrc from '@hm-group/frontend-utils/image/imageIdToAkamaiSrc';
import toHighDPIImageUrl from '@hm-group/frontend-utils/url/toHighDPIImageUrl';
import { useBreakpoint } from "@hm-group/frontend-utils-react/hooks";
import cn from 'classnames';
import getResolverDataAttributes from './helpers/getResolverDataAttributes';
import getResolverPlaceholder from './helpers/getResolverPlaceholder';
import ImageDefaultPropsContext from './helpers/ImageDefaultPropsContext';
import "../../Image.css";
var styles = { "image": "c63693", "imageWrapper": "fec837", "reserveHeight": "bd9359", "backgroundImage": "b8da44", "skeleton": "d3f72d", "backgroundSpan": "ba6cb5" };
import BackgroundImage from '../BackgroundImage/index';
import DefaultImage from '../DefaultImage/index';
const ClientImage = (props) => {
    const defaultProps = useContext(ImageDefaultPropsContext);
    const allProps = Object.assign(Object.assign({}, defaultProps), props);
    const { altText, url, className, shouldLazyLoad = true, imageType, isBackgroundImage = false, reserveHeightAspectRatio = '', height, width, shouldShowSkeleton = true, resolverSettings, shouldUseResolver = false, shouldUseHighDPIImage, shouldUseDirectSrc, srcSet, sizes, imageId, cdnType, isImageHeightAutoAdjustmentEnabled = false, shouldDelayLazyLoad = false, // prevent jittery scrolling by delaying lazy image loading
    lazyLoadDelayMs = 500, // default is current carousel scroll time default
    onLoad, } = allProps;
    const shouldUseAkamaiSrc = cdnType === 'Akamai' && imageId;
    const initialImageSrc = imageType || shouldUseAkamaiSrc ? '' : url;
    const [isIntersected, setIsIntersected] = useState(false);
    const srcSetProps = shouldLazyLoad && !isIntersected
        ? { srcSet: '', src: '' }
        : {
            srcSet,
            src: url,
        };
    const [isLoading, setIsLoading] = useState(true);
    const [imageSrc, setImageSrc] = useState(initialImageSrc);
    const [lazyImageUrl, setLazyImageUrl] = useState('');
    const [delayedSrc, setDelayedSrc] = useState(undefined);
    // If we are lazyLoading, use the lazyImageUrl state, else use the imageSrc immediately.
    const handledSrc = useMemo(() => {
        // If we are lazyLoading, set empty string '' as src, when image is scrolled into view (intersected) set imageSrc as src. Else use imageSrc immediately.
        const handledUrl = shouldLazyLoad && !isIntersected ? '' : imageSrc;
        return shouldUseHighDPIImage ? toHighDPIImageUrl(handledUrl) : handledUrl;
    }, [shouldUseHighDPIImage, shouldLazyLoad, isIntersected, imageSrc]);
    useEffect(() => {
        if (shouldDelayLazyLoad && isIntersected) {
            setTimeout(() => {
                setDelayedSrc(handledSrc);
            }, lazyLoadDelayMs);
        }
    }, [isIntersected]);
    const imgRef = createRef();
    const backgroundImageRef = createRef();
    const refElement = isBackgroundImage ? backgroundImageRef : imgRef;
    const useDevPrefixedUrl = process.env.USE_DEV_PREFIXED_LIQUID_PIXELS_URLS === 'enabled';
    const isXLarge = useBreakpoint() === breakpoints.xlarge;
    const isLifeArticle = () => {
        if (className && isXLarge) {
            if (className.split(' ').filter((c) => c === 'life-article-image').length) {
                return true;
            }
        }
        return false;
    };
    // Get adjusted resolution image url for image src based on container width and image type. If image type is undefined, default image-sizes will be used.
    const getImageSrc = (src, containerWidth, type) => {
        if (shouldUseDirectSrc)
            return src;
        return shouldUseAkamaiSrc
            ? imageIdToAkamaiSrc(imageId, containerWidth, undefined)
            : getImageUrlBasedOnContainerWidth({
                src,
                type,
                containerWidth,
                useDevPrefixedUrl,
            });
    };
    const setAdjustedResolution = (src, parent) => {
        const parentWidth = parent.offsetWidth;
        if (parentWidth === 0) {
            setTimeout(() => {
                setAdjustedResolution(src, parent);
            }, 100);
        }
        else {
            const adjustedImageSrc = getImageSrc(src, parentWidth < 160 ? 160 : parentWidth, imageType);
            setImageSrc(adjustedImageSrc);
        }
    };
    const handleImageLoaded = () => {
        // If image src value is not empty we set loading to false onLoad. onLoad runs even if img src attribute is empty.
        if (shouldDelayLazyLoad && delayedSrc) {
            setIsLoading(false);
        }
        else if (handledSrc) {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        var _a;
        if ((_a = imgRef.current) === null || _a === void 0 ? void 0 : _a.complete) {
            handleImageLoaded();
        }
    }, []);
    const handleOnShowLazyImg = (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                setIsIntersected(true);
            }
        });
    };
    useEffect(() => {
        if (refElement && refElement.current && (url || imageId) && imageType && !shouldUseResolver) {
            if (url) {
                setAdjustedResolution(url, refElement.current.parentNode);
            }
        }
        else if (refElement && refElement.current && (url || imageId) && !imageType) {
            const imageUrl = url !== undefined ? url : imageIdToAkamaiSrc(imageId, height, width);
            setImageSrc(imageUrl);
            if (shouldLazyLoad && lazyImageUrl) {
                setLazyImageUrl(imageUrl);
            }
        }
    }, [url, refElement.current, imageType, shouldLazyLoad]);
    useEffect(() => {
        if (!isLoading) {
            setIsLoading(true);
        }
    }, [imageSrc]);
    if (!(url || imageId))
        return null;
    const getReserveHeightStyle = () => {
        if (isImageHeightAutoAdjustmentEnabled) {
            return { height: `auto` };
        }
        if (isLifeArticle()) {
            return { paddingBottom: `calc(1140px / (${reserveHeightAspectRatio}))` };
        }
        return { paddingBottom: `calc(100% / (${reserveHeightAspectRatio}))` };
    };
    const reserveHeightStyle = getReserveHeightStyle();
    const srcDelayLazyLoad = shouldDelayLazyLoad ? delayedSrc : handledSrc;
    const renderImg = (React.createElement("img", Object.assign({ ref: imgRef, className: cn(styles.image, shouldUseResolver && 'Resolve', props.imageClassName), onLoad: () => {
            handleImageLoaded();
            if (onLoad)
                onLoad();
        }, onError: handleImageLoaded, alt: altText, sizes: sizes, loading: props === null || props === void 0 ? void 0 : props.loading }, (srcSet && srcSetProps), { src: shouldUseResolver ? getResolverPlaceholder(url, resolverSettings) : srcDelayLazyLoad }, (height && !reserveHeightAspectRatio && { height }), (width && !reserveHeightAspectRatio && { width }), (shouldUseResolver && getResolverDataAttributes(url, resolverSettings)))));
    const renderSkeleton = isLoading && shouldShowSkeleton && (React.createElement(FullSizeSkeleton, { isAnimationDisabled: false }));
    if (isBackgroundImage)
        return (React.createElement(BackgroundImage, Object.assign({ handleOnShowLazyImg: handleOnShowLazyImg, isIntersected: isIntersected, backgroundImageRef: backgroundImageRef, Tag: allProps.Tag }, allProps)));
    if (!isBackgroundImage)
        return (React.createElement(DefaultImage, Object.assign({ handleOnShowLazyImg: handleOnShowLazyImg, isIntersected: isIntersected, renderImg: renderImg, renderSkeleton: renderSkeleton, reserveHeightStyle: reserveHeightStyle, Tag: allProps.Tag }, allProps)));
    return null;
};
export default ClientImage;
//# sourceMappingURL=ClientImage.js.map